import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdCheckCircle, MdErrorOutline } from "react-icons/md";
import AtriumApi from "api";
import { useAuth } from "hooks/useAuth";
import Card from "components/card";
import Loader from "components/loader/Loader";
import AlertPane from "components/alert";
import { PageContainer, PageHeader } from "components/page";

/**
 * Auth Callback Page
 * Handles secure token exchange from Spark onboarding
 *
 * Flow:
 * 1. User logs in at Spark (signup.atriumai.co)
 * 2. Spark generates one-time auth code
 * 3. Spark redirects here with code in URL
 * 4. This page exchanges code for JWT token
 * 5. Stores token securely in localStorage
 * 6. Redirects to dashboard
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const exchangeAuthCode = async () => {
      try {
        const code = searchParams.get("code");

        if (!code) {
          setStatus("error");
          setErrorMessage("No authorization code provided");
          return;
        }

        setStatus("processing");

        // Exchange the one-time code for a JWT token
        const response = await AtriumApi.post("/v1/auth/exchange-code", {
          code,
        });

        if (response?.data?.success && response?.data?.data?.token) {
          const { token, user } = response.data.data;

          // Also store with alternate keys for compatibility
          localStorage.setItem("atriumAuthToken", token);
          localStorage.setItem("atriumUser", JSON.stringify(user));

          if (user.organization_slug) {
            localStorage.setItem("atriumChurchSlug", user.organization_slug);
          }

          setStatus("success");

          // Use the login function from useAuth to update context and navigate
          // This will store the token, decode it, and redirect to dashboard
          setTimeout(() => {
            login(token);
          }, 500);
        } else {
          setStatus("error");
          setErrorMessage("Invalid response from server");
        }
      } catch (error) {
        console.error("Error exchanging auth code:", error);
        setStatus("error");

        if (error.response?.status === 404) {
          setErrorMessage("Invalid authorization code");
        } else if (error.response?.status === 403) {
          setErrorMessage("Authorization code has already been used");
        } else if (error.response?.status === 410) {
          setErrorMessage("Authorization code has expired. Please log in again.");
        } else {
          setErrorMessage(
            error.response?.data?.message || "Failed to complete authentication"
          );
        }

        // Redirect to login after showing error
        setTimeout(() => {
          const onboardingUrl = process.env.REACT_APP_ONBOARDING_URL || "http://localhost:5173";
          window.location.href = `${onboardingUrl}/login`;
        }, 3000);
      }
    };

    exchangeAuthCode();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <Card padding="lg" className="w-full max-w-md">
        <PageContainer className="items-center text-center">
          {status === "processing" && (
            <>
              <Loader size="lg" variant="inline" label="Completing sign-in" />
              <PageHeader
                title="Completing Sign-In"
                description="Securely authenticating your session…"
              />
            </>
          )}

          {status === "success" && (
            <>
              <MdCheckCircle aria-hidden="true" className="h-16 w-16 text-success-fg" />
              <PageHeader
                title="Authentication Successful"
                description="Redirecting to your dashboard…"
              />
            </>
          )}

          {status === "error" && (
            <>
              <MdErrorOutline aria-hidden="true" className="h-16 w-16 text-danger-fg" />
              <PageHeader
                title="Authentication Failed"
                description="Redirecting to the login page…"
              />
              <AlertPane tone="danger" className="w-full text-left">
                {errorMessage}
              </AlertPane>
            </>
          )}
        </PageContainer>
      </Card>
    </div>
  );
};

export default AuthCallback;
